import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Search, Loader2, CalendarDays, CheckCircle2,
  Clock, XCircle, CreditCard, ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  confirmed: { label: 'Confirmed', icon: CheckCircle2, color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  pending_payment: { label: 'Pending Payment', icon: Clock, color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  expired: { label: 'Expired', icon: XCircle, color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  cancelled: { label: 'Cancelled', icon: XCircle, color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
};

const formatDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'long', year: 'numeric',
  });
};

export default function MyBooking() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    setError('');
    setResults(null);

    const isEmail = q.includes('@');
    const param = isEmail ? `email=${encodeURIComponent(q)}` : `ref=${encodeURIComponent(q)}`;

    try {
      const res = await fetch(`${API_URL}/api/bookings/lookup?${param}`);
      if (!res.ok) throw new Error('Search failed');
      const data = await res.json();
      setResults(data);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 pt-28 pb-20">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#94a3b8] hover:text-white transition mb-8 text-sm"
          data-testid="back-home-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="text-center mb-10">
          <Badge className="bg-[#e2e8f0] text-slate-800 mb-4 px-4 py-1.5 text-xs font-semibold">
            MY BOOKING
          </Badge>
          <h1 className="text-3xl md:text-4xl font-bold text-white heading-font mb-3">
            Check Your Booking
          </h1>
          <p className="text-gray-400 text-sm">
            Enter your booking reference (e.g. AA-20260619-ZF9Y) or the email address you used to book.
          </p>
        </div>

        {/* Search form */}
        <Card className="bg-[#1e293b] border-slate-700 p-6 mb-8" data-testid="booking-lookup-card">
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="flex-1">
              <Label htmlFor="lookup" className="sr-only">Booking ref or email</Label>
              <Input
                id="lookup"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Booking ref or email address"
                className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 h-11"
                data-testid="booking-lookup-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading || !query.trim()}
              className="bg-[#e2e8f0] text-slate-900 hover:bg-[#cbd5e1] h-11 px-5"
              data-testid="booking-lookup-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </form>
        </Card>

        {/* Error */}
        {error && (
          <div className="text-center text-red-400 text-sm mb-6" data-testid="lookup-error">{error}</div>
        )}

        {/* Results */}
        {results !== null && (
          <div data-testid="lookup-results">
            {results.length === 0 ? (
              <Card className="bg-[#1e293b] border-slate-700 p-8 text-center">
                <Search className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-white heading-font mb-1">No Bookings Found</h3>
                <p className="text-gray-400 text-sm">
                  We couldn&apos;t find any bookings matching that. Double-check your reference or email and try again.
                </p>
              </Card>
            ) : (
              <div className="space-y-4">
                {results.map((b) => {
                  const cfg = statusConfig[b.status] || statusConfig.expired;
                  const StatusIcon = cfg.icon;
                  return (
                    <Card
                      key={b.booking_ref}
                      className="bg-[#1e293b] border-slate-700 p-5"
                      data-testid={`booking-result-${b.booking_ref}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Booking Reference</p>
                          <p className="text-lg font-mono font-bold text-white tracking-wider" data-testid="result-booking-ref">
                            {b.booking_ref}
                          </p>
                        </div>
                        <Badge className={`${cfg.color} border text-xs flex items-center gap-1`}>
                          <StatusIcon className="w-3 h-3" />
                          {cfg.label}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500 text-xs mb-0.5">Package</p>
                          <p className="text-gray-200 font-medium">{b.package_name}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs mb-0.5">Event Date</p>
                          <p className="text-gray-200 font-medium flex items-center gap-1.5">
                            <CalendarDays className="w-3.5 h-3.5 text-[#94a3b8]" />
                            {formatDate(b.event_date)}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs mb-0.5">Name</p>
                          <p className="text-gray-200">{b.customer_name}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs mb-0.5">Deposit</p>
                          <p className="text-gray-200 font-medium flex items-center gap-1.5">
                            <CreditCard className="w-3.5 h-3.5 text-[#94a3b8]" />
                            &pound;{(b.deposit_pence / 100).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
